import { EventEmitter, inject, Injectable } from '@angular/core';

import { BrandingTheme, CustomerBranding } from '@ui/shared/model';

import { ImmomioThemeService } from '@immomio/ui/theme';
import { ENVIRONMENT_CONFIG } from '../../tokens/environment-injection-token';
import {
  ACTIVE_THEME,
  Theme,
  ThemeProperties,
  ThemePropertyKeys,
  THEMES
} from './symbols';
import { ThemeUrls } from './theme-urls';
import {
  adjustColorLightness,
  getColorDark,
  getColorPrimaryAccentLight,
  getColorSecondaryAccentLight,
  getContrastingBWColor
} from './theme.utils';
import { ImmomioTheme } from './themes/immomioTheme';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  themes = inject(THEMES);
  theme = inject(ACTIVE_THEME);
  environment = inject(ENVIRONMENT_CONFIG);
  #immomioThemeSevice = inject(ImmomioThemeService);

  public themeChange = new EventEmitter<Theme>();
  private defaultTheme: string = ThemeUrls.IMMOMIO;

  public static processName(name: string) {
    return name?.toLowerCase()?.replace(/ /g, '-');
  }

  public getTheme(name: string = ThemeUrls.IMMOMIO) {
    const theme = this.findTheme(name);
    if (!theme) {
      throw new Error(`Theme not found: '${name.toLowerCase()}'`);
    }
    return theme;
  }

  public get getDefaultTheme() {
    return this.defaultTheme;
  }

  public get getActiveTheme() {
    return this.getTheme(this.theme);
  }

  public setTheme(name: string) {
    this.theme = name;
    if (
      ThemeService.processName(name) ===
      ThemeService.processName(this.defaultTheme)
    ) {
      // A landlord theme also writes `--im-color-*` onto the document root, so
      // dropping it has to clear those as well - the legacy theme properties
      // are only half of the branding.
      this.#immomioThemeSevice.resetTheme();
    }
    this.themeChange.emit(this.getActiveTheme);
  }

  /**
   * Drops a landlord branding again: switches back to the Immomio theme, which
   * lets the design system remove the whitelabel custom properties written by
   * `createTheme`, so its own defaults apply again.
   */
  public resetTheme() {
    this.setTheme(this.defaultTheme);
  }

  public registerTheme(theme: Theme) {
    const index = this.themes.findIndex(t => t.name === theme.name);
    if (theme.name === this.getActiveTheme.name && index >= 0) {
      this.themes[index] = theme;
      this.themeChange.emit(theme);
      return;
    }
    this.themes.push(theme);
  }

  public createTheme(brandingTheme: CustomerBranding) {
    if (!brandingTheme?.theme) return;
    const { logo, favicon, iTPLogo, theme } = brandingTheme;
    if (this.findTheme(brandingTheme?.theme?.name)) {
      return this.updateTheme(theme);
    }
    const landlordTheme = {
      name: ThemeService.processName(theme?.name),
      logo: logo,
      favicon: favicon,
      iTPLogo: iTPLogo,
      properties: this.updateThemeProperty(theme)
    };

    this.registerTheme(landlordTheme);
    this.setTheme(landlordTheme.name);
    this.#immomioThemeSevice.setTheme({
      ...theme,
      containerBackgroundColor: theme.cardBackgroundColor
    });
  }

  private updateTheme(newTheme: BrandingTheme) {
    const theme = this.getTheme(ThemeService.processName(newTheme.name));
    theme.properties = this.updateThemeProperty(newTheme);
    const index = this.themes.findIndex(
      t =>
        ThemeService.processName(t.name) ===
        ThemeService.processName(newTheme.name)
    );
    this.themes[index] = theme;
    if (ThemeService.processName(newTheme.name) === this.theme) {
      this.themeChange.emit(theme);
    }
    this.#immomioThemeSevice.setTheme({
      ...newTheme,
      containerBackgroundColor: newTheme.cardBackgroundColor
    });
  }

  private updateThemeProperty(theme: BrandingTheme): ThemeProperties {
    const colorPrimaryAccentLight = getColorPrimaryAccentLight(
      theme.primaryColor
    );
    const colorPrimaryAccentDark = getColorDark(theme.primaryColor);
    const colorSecondaryAccentLight = getColorSecondaryAccentLight(
      theme.secondaryColor
    );
    const colorSecondaryAccentLighter = adjustColorLightness(
      theme.secondaryColor,
      0.93
    );
    const colorSecondaryAccentDark = getColorDark(theme.secondaryColor);

    return {
      ...ImmomioTheme.properties,

      [ThemePropertyKeys.THEME_COLOR_APP_BACKGROUND]: theme.backgroundColor,
      [ThemePropertyKeys.THEME_COLOR_CARD_BACKGROUND]:
        theme.cardBackgroundColor,

      [ThemePropertyKeys.THEME_COLOR_PRIMARY_ACCENT]: theme.primaryColor,
      [ThemePropertyKeys.THEME_COLOR_PRIMARY_ACCENT_BW_CONTRAST]:
        getContrastingBWColor(theme.primaryColor),
      [ThemePropertyKeys.THEME_COLOR_PRIMARY_ACCENT_LIGHT]:
        colorPrimaryAccentLight,
      [ThemePropertyKeys.THEME_COLOR_PRIMARY_ACCENT_LIGHT_BW_CONTRAST]:
        getContrastingBWColor(colorPrimaryAccentLight),
      [ThemePropertyKeys.THEME_COLOR_PRIMARY_ACCENT_DARK]:
        colorPrimaryAccentDark,
      [ThemePropertyKeys.THEME_COLOR_PRIMARY_ACCENT_DARK_BW_CONTRAST]:
        getContrastingBWColor(colorPrimaryAccentDark),

      [ThemePropertyKeys.THEME_COLOR_SECONDARY_ACCENT]: theme.secondaryColor,
      [ThemePropertyKeys.THEME_COLOR_SECONDARY_ACCENT_BW_CONTRAST]:
        getContrastingBWColor(theme.secondaryColor),
      [ThemePropertyKeys.THEME_COLOR_SECONDARY_ACCENT_LIGHT]:
        colorSecondaryAccentLight,
      [ThemePropertyKeys.THEME_COLOR_SECONDARY_ACCENT_LIGHT_BW_CONTRAST]:
        getContrastingBWColor(colorSecondaryAccentLight),
      [ThemePropertyKeys.THEME_COLOR_SECONDARY_ACCENT_LIGHTER]:
        colorSecondaryAccentLighter,
      [ThemePropertyKeys.THEME_COLOR_SECONDARY_ACCENT_LIGHTER_BW_CONTRAST]:
        getContrastingBWColor(colorSecondaryAccentLighter),
      [ThemePropertyKeys.THEME_COLOR_SECONDARY_ACCENT_DARK]:
        colorSecondaryAccentDark,
      [ThemePropertyKeys.THEME_COLOR_SECONDARY_ACCENT_DARK_BW_CONTRAST]:
        getContrastingBWColor(colorSecondaryAccentDark),

      [ThemePropertyKeys.THEME_COLOR_PRIMARY_TEXT]: theme.primaryTextColor,
      [ThemePropertyKeys.THEME_COLOR_SECONDARY_TEXT]: theme.secondaryTextColor,
      [ThemePropertyKeys.THEME_COLOR_BUTTON_TEXT]: theme.buttonTextColor,
      [ThemePropertyKeys.THEME_COLOR_MENU_BAR_BACKGROUND]:
        theme.menuBarBackgroundColor || theme.cardBackgroundColor,
      [ThemePropertyKeys.THEME_COLOR_MENU_BAR_ITEM_ACTIVE]:
        theme.menuBarTextColorActive || theme.primaryColor,
      [ThemePropertyKeys.THEME_COLOR_MENU_BAR_ITEM_INACTIVE]:
        theme.menuBarTextColorInactive || '#000000'
    };
  }

  private findTheme(name: string) {
    return this.themes?.find(
      t => ThemeService.processName(t?.name) === ThemeService.processName(name)
    );
  }
}
